import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import MenuLogged from "../../components/MenuLogged/MenuLogged"
import { companyMemory, courierMemory, orderMemory } from "../../Objects/Memory/Memory"
import { OrderEntity } from "../../Objects/Order/OrderEntity"
import { CourierEntity } from "../../Objects/Courier/CourierEntity"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { EOrderCurrentStatus } from "../../Enum/EOrderCurrentStatus"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { EShippingCalculationMethodType } from "../../Enum/EShippingCalculationMethodType"
import { useGetOrder } from "../../hooks/Order/useGetOrder"
import { useSaveOrder } from "../../hooks/Order/useSaveOrder"
import { useSaveDelivery } from "../../hooks/Delivery/useSaveDelivery"
import { useGetCourier } from "../../hooks/Courier/useGetCourier"
import { useStoreDeliveryHub } from "../../hooks/Delivery/useStoreDeliveryHub"
import { useError } from "../../context/useErrorContext"
import { getStoreNow } from "../../utils/timeZone"
import { AssignCourierPopup } from "./AssignCourierPopup"
import { FulfillmentOrderCard } from "./Components/FulfillmentOrderCard"
import { FulfillmentStation, getFulfillmentStationItems } from "./fulfillmentOrderCounters"

const notificationSound = new Audio("/sounds/newOrder.wav")
notificationSound.volume = 1

const getDefaultDateRange = () => {
  const dateEnd = getStoreNow(companyMemory.timeZoneId)
  const dateStart = getStoreNow(companyMemory.timeZoneId)
  dateStart.setDate(dateStart.getDate() - 1)
  return { dateStart: dateStart.toInputDate(), dateEnd: dateEnd.toInputDate() }
}

interface FulfillmentPageProps {
  station?: FulfillmentStation
}

export const FulfillmentPage: React.FC<FulfillmentPageProps> = ({ station = "FULFILLMENT" }) => {
  const { t } = useTranslation("fulfillment")
  const [searchParams, setSearchParams] = useSearchParams()
  const isDelayedView = searchParams.get("showDelayed") === "true"
  const [orders, setOrders] = useState<OrderEntity[]>([])
  const [appliedDateRange, setAppliedDateRange] = useState(getDefaultDateRange)
  const [draftDateRange, setDraftDateRange] = useState(getDefaultDateRange)
  const [isFilterExpanded, setIsFilterExpanded] = useState(false)
  const [filter, setFilter] = useState<EOrderStatusType | "ALL">(isDelayedView ? "ALL" : EOrderStatusType.Pending)
  const [hasNewPending, setHasNewPending] = useState(false)
  const [assignOrder, setAssignOrder] = useState<OrderEntity | null>(null)
  const [couriers, setCouriers] = useState<CourierEntity[]>([])
  const [selectedCourierId, setSelectedCourierId] = useState("")
  const pendingCountRef = useRef(0)
  const handleGetOrderRef = useRef<ReturnType<typeof useGetOrder>["handleGetOrder"] | null>(null)
  const { setError } = useError()
  const { handleGetOrder } = useGetOrder()
  const { handleSaveOrderStatus } = useSaveOrder()
  const { handleAssignDelivery, handleCancelSearchDelivery } = useSaveDelivery()
  const { handleGetCourier } = useGetCourier()
  const mode = companyMemory.fulfillmentMode

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
  }, [handleGetOrder])

  const getStationItems = useCallback(
    (order: OrderEntity) => getFulfillmentStationItems(order, station, mode),
    [station, mode],
  )

  const fetchOrders = useCallback(() => {
    setHasNewPending(false)
    handleGetOrderRef.current?.(
      () => {
        const data = orderMemory.listData
        const pending = data.filter((order) =>
          getStationItems(order).some((item) => item.currentStatus === EOrderCurrentStatus.Pending),
        ).length
        if (pending > pendingCountRef.current) {
          setHasNewPending(true)
          void notificationSound.play().catch(() => undefined)
        }
        pendingCountRef.current = pending
        setOrders(data)
      },
      appliedDateRange.dateStart,
      appliedDateRange.dateEnd,
      undefined,
      undefined,
      false,
      isDelayedView,
    )
  }, [appliedDateRange, getStationItems, isDelayedView])

  useStoreDeliveryHub(fetchOrders)

  useEffect(() => {
    queueMicrotask(fetchOrders)
    const interval = setInterval(fetchOrders, 30000)
    return () => clearInterval(interval)
  }, [fetchOrders, isDelayedView])

  const activeOrdersCount = orders.filter((order) => order.eOrderStatusType !== EOrderStatusType.Delivered).length
  const stationOrders = orders.filter((order) => {
    const items = getStationItems(order)
    if (items.length === 0) {
      return false
    }
    if (filter === "ALL") {
      return items.some((item) => item.currentStatus !== EOrderCurrentStatus.Closed)
    }
    if (filter === EOrderStatusType.InDelivery || filter === EOrderStatusType.Delivered) {
      return order.eOrderStatusType === filter
    }
    return items.some((item) => item.currentStatus === filter || item.currentStatus === EOrderCurrentStatus.Closed)
  })

  const tabCount = (status: EOrderStatusType) =>
    orders.filter((order) =>
      status === EOrderStatusType.InDelivery
        ? order.eOrderStatusType === status
        : getStationItems(order).some((item) => item.currentStatus === status),
    ).length

  const openAssignCourier = (order: OrderEntity) => {
    if (!order.addressEntity) {
      setError(t("address_not_found"), t("address_not_found"))
      return
    }
    setAssignOrder(order)
    setSelectedCourierId("")
    handleGetCourier(() => setCouriers(courierMemory.listData.map((courier) => new CourierEntity(courier))))
  }

  const confirmAssign = () => {
    if (!assignOrder) {
      return
    }
    const selectedCourier = selectedCourierId ? couriers.find((courier) => courier.id === selectedCourierId) : undefined
    if (selectedCourierId && selectedCourier?.status !== ECourierStatusType.Online) {
      return
    }
    handleAssignDelivery(
      {
        orderId: assignOrder.id,
        courierEntityId: selectedCourierId || undefined,
        hasAcceptedFinancialResponsibility: true,
      },
      () => {
        setAssignOrder(null)
        fetchOrders()
      },
    )
  }

  const tabs = [
    { label: t("pending"), value: EOrderStatusType.Pending },
    { label: t("preparing"), value: EOrderStatusType.Preparing },
    { label: t("ready_for_delivery"), value: EOrderStatusType.PreparingFinished },
    { label: t("out_for_delivery"), value: EOrderStatusType.InDelivery },
    { label: t("finished"), value: EOrderStatusType.Delivered },
    { label: t("all_not_finished"), value: "ALL" as const },
  ]

  return (
    <Animate type={AnimationTypes.fadeIn}>
      <div className="flex h-dvh flex-col bg-white font-inter">
        <MenuLogged enableButtons={false} />
        <main className="flex-1 overflow-y-auto p-2 md:p-6">
          <div className="mb-4 space-y-2">
            <div className="flex flex-wrap gap-2 rounded-lg bg-slate-100 p-2 ring-2 ring-slate-300">
              {tabs.map((tab) => (
                <button
                  key={tab.label}
                  onClick={() => {
                    setFilter(tab.value)
                    setHasNewPending(false)
                  }}
                  className={`relative rounded-lg px-4 py-2 text-sm font-bold text-slate-900 transition ${
                    filter === tab.value ? "bg-white shadow-sm ring-2 ring-slate-400" : "hover:bg-white"
                  } ${tab.value === EOrderStatusType.Pending && hasNewPending ? "animate-pulse bg-orange-200 ring-2 ring-orange-700" : ""}`}

                  data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.fulfillmentPage.button.setFilterTabValueSetHasNewPendingFalse">
                  {tab.label}
                  {typeof tab.value === "number" && tabCount(tab.value) > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white ring-1 ring-red-800">
                      {tabCount(tab.value) > 99 ? "99+" : tabCount(tab.value)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-100 p-3 ring-2 ring-slate-300">
              <div className="flex flex-1 flex-wrap gap-3">
                {isDelayedView && (
                  <div className="rounded-lg bg-red-50 px-4 py-2 text-sm font-bold text-red-800 ring-2 ring-red-200">
                    {t("delayed_filter_active")}
                  </div>
                )}
                <div className="rounded-lg bg-white px-4 py-2 text-sm text-slate-700 ring-2 ring-slate-200">
                  {t("active")}: <strong>{activeOrdersCount}</strong>
                </div>
                <div className="rounded-lg bg-white px-4 py-2 text-sm text-slate-700 ring-2 ring-slate-200">
                  {t("showing")}: <strong>{stationOrders.length}</strong>
                </div>
              </div>
              <button
                onClick={() => setIsFilterExpanded((current) => !current)}
                className="flex h-10 w-10 items-center justify-center rounded-md bg-white ring-2 ring-slate-200 lg:hidden"

                  data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.fulfillmentPage.button.setIsFilterExpandedCurrentCurrent">
                <FontAwesomeIcon icon={isFilterExpanded ? faAngleUp : faAngleDown} />
              </button>
              <div
                className={`${isFilterExpanded ? "grid" : "hidden"} w-full gap-3 sm:grid-cols-[minmax(150px,220px)_minmax(150px,220px)_auto] lg:ml-auto lg:grid lg:w-auto`}
              >
                <InputField
                  type="date"
                  value={draftDateRange.dateStart}
                  onChange={(event) => setDraftDateRange((current) => ({ ...current, dateStart: event.target.value }))}
                  placeholder={t("date_start")}

                  descriptionKey="interactionDescriptions:pages.fulfillmentPage.fulfillmentPage.input.tDateStart" />
                <InputField
                  type="date"
                  value={draftDateRange.dateEnd}
                  onChange={(event) => setDraftDateRange((current) => ({ ...current, dateEnd: event.target.value }))}
                  placeholder={t("date_end")}

                  descriptionKey="interactionDescriptions:pages.fulfillmentPage.fulfillmentPage.input.tDateEnd" />
                <button
                  onClick={() => {
                    const normalized =
                      draftDateRange.dateEnd < draftDateRange.dateStart
                        ? { dateStart: draftDateRange.dateEnd, dateEnd: draftDateRange.dateStart }
                        : draftDateRange
                    setAppliedDateRange(normalized)
                    if (isDelayedView) setSearchParams({})
                  }}
                  className="h-11 rounded-md bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-700"

                  data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.fulfillmentPage.button.constNormalizedDraftDateRangeDateEndDraftDateRangeDateStartDateStartDraftDateRangeDateEndDateEndDraftDateRangeDateStartDraftDateRangeSetAppliedDateRangeNormalizedIfIsDelayedViewSetSearchParams">
                  {t("apply_filter")}
                </button>
              </div>
            </div>
          </div>

          {stationOrders.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
              {stationOrders.map((order) => (
                <FulfillmentOrderCard
                  key={order.id}
                  order={order}
                  type={filter}
                  station={station}
                  mode={mode}
                  onUpdate={fetchOrders}
                  onUpdateStatus={(id, status) => handleSaveOrderStatus(id, status, fetchOrders)}
                  onSelectCourier={openAssignCourier}
                  onCancelSearch={(deliveryId) => handleCancelSearchDelivery(deliveryId, fetchOrders)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-slate-300">
              <p className="text-lg font-semibold uppercase tracking-widest">{t("clean_station")}</p>
            </div>
          )}
        </main>

        <AssignCourierPopup
          isOpen={!!assignOrder}
          couriers={couriers}
          selectedCourierId={selectedCourierId}
          allowRandomCourier={
            assignOrder?.orderPaymentEntity?.shippingCalculationMethod !== EShippingCalculationMethodType.Free &&
            (assignOrder?.orderPaymentEntity?.shippingFee ?? 0) > 0
          }
          onClose={() => setAssignOrder(null)}
          onSelectCourier={setSelectedCourierId}
          onConfirmSelected={confirmAssign}
          onConfirmRandom={confirmAssign}
        />

        <footer className="flex flex-col gap-2 border-t bg-white px-6 py-4 text-xs text-slate-500 md:flex-row md:justify-between">
          <span className="font-bold text-green-600">{t("connected")}</span>
          <span className="font-bold text-gray-600">
            {t("last_update")} {getStoreNow(companyMemory.timeZoneId).toLocaleTimeString()}
          </span>
        </footer>
      </div>
    </Animate>
  )
}
